import { Card, Group, Stack, useMantineTheme } from '@mantine/core';
import { trackEvent } from '@repo/common/analytics';
import { SelectorTarget, SelectType } from '@repo/common/components';
import { Selector } from '@repo/common/components/Selectors/Selector';
import { IconButton, Title } from '@repo/foundations';
import { memo, useCallback, useMemo } from 'react';
import { useAuthUser } from '../../api';
import type { DateRange } from '../../components/DateRangeButtonGroup/DateRangeButtonGroup';
import DateRangeButtonGroup from '../../components/DateRangeButtonGroup/DateRangeButtonGroup';
import { MetricsOverviewStackedLineChart } from '../../features/Overview/components/charts/MetricsOverviewStackedLineChart';
import type { StackedLineChartChannel } from '../../utils/charts/StackedLineChart';

export interface BaseOverviewChartProps<
	TDataPoint extends Record<string, unknown> & { date: Date },
> {
	timeseries: {
		points: Array<{
			timestamp: string;
			[key: string]: unknown;
		}>;
		granularity: string;
		available_channels: string[];
		available_dimensions: string[];
	};
	dateFilter: string;
	setDateFilter: (dateFilter: string) => void;
	setTimeRangeValue: (timeRangeValue: {
		timeRange: string;
		dateRange: { from: Date | null; to: Date | null };
	}) => void;
	timeRangeValue: {
		timeRange: string;
		dateRange: { from: Date | null; to: Date | null };
	};
	dateRangeOption: string;
	setDateRangeOption: (dateRangeOption: string) => void;
	title: string;
	dateFilterOptions: Array<{ value: string; label: string }>;
	channels: StackedLineChartChannel<TDataPoint, Date, number>[];
	tooltipDataRenderer: (dataIndex: number) => JSX.Element | null;
	analyticsEventPrefix: string;
	onExportCsv?: () => void;
	showExportButton?: boolean;
	yAxisFormatter?: (value: number) => string;
	hideDateFilter?: boolean;
	hideDateRangeButtons?: boolean;
	onClick?: (dataIndex: number) => void;
	showEmptyStateMessage?: boolean;
	emptyStateMessage?: { title: string; description: string };
	maxChannels?: number;
}

export const BaseOverviewChart = memo(
	<TDataPoint extends Record<string, unknown> & { date: Date }>({
		timeseries,
		dateFilter,
		setDateFilter,
		setTimeRangeValue,
		timeRangeValue,
		dateRangeOption,
		setDateRangeOption,
		title,
		dateFilterOptions,
		channels,
		tooltipDataRenderer,
		analyticsEventPrefix,
		onExportCsv,
		showExportButton = false,
		yAxisFormatter,
		hideDateFilter = false,
		hideDateRangeButtons = false,
		onClick,
		showEmptyStateMessage,
		emptyStateMessage,
		maxChannels,
	}: BaseOverviewChartProps<TDataPoint>) => {
		const { user, workspace } = useAuthUser();
		const theme = useMantineTheme();

		const chartData = useMemo(() => {
			if (!timeseries?.points) return [];

			const sortedData = timeseries.points
				.map((point) => ({
					...point,
					timestamp: new Date(point.timestamp),
					date: new Date(point.timestamp), // Add date property for MetricsOverviewStackedLineChart
				}))
				.sort(
					(a, b) => a.timestamp.getTime() - b.timestamp.getTime()
				) as unknown as TDataPoint[];

			return sortedData;
		}, [timeseries]);

		const handleDateFilterChange = useCallback(
			(values: string[]) => {
				const newDateFilter = values[0] as string;
				setDateFilter(newDateFilter);
				setTimeRangeValue({
					...timeRangeValue,
					timeRange: newDateFilter,
				});
				trackEvent(
					`${analyticsEventPrefix}/date_filter_change`,
					{
						date_filter: newDateFilter,
					},
					user,
					workspace!
				);
			},
			[
				setDateFilter,
				setTimeRangeValue,
				timeRangeValue,
				user,
				workspace,
				analyticsEventPrefix,
			]
		);

		const handleDateRangeChange = useCallback(
			(range: DateRange, optionId: string) => {
				setTimeRangeValue({
					timeRange: dateFilter,
					dateRange: range,
				});
				setDateRangeOption(optionId);
				trackEvent(
					`${analyticsEventPrefix}/date_range_change`,
					{
						date_range_option: optionId,
						date_range_from: range.from?.toISOString() ?? '',
						date_range_to: range.to?.toISOString() ?? '',
					},
					user,
					workspace!
				);
			},
			[
				dateFilter,
				setTimeRangeValue,
				setDateRangeOption,
				user,
				workspace,
				analyticsEventPrefix,
			]
		);

		const getDateFilterItems = useCallback(
			async () => dateFilterOptions,
			[dateFilterOptions]
		);

		const selectedDateFilterLabel = useMemo(
			() =>
				dateFilterOptions.find((item) => item.value === dateFilter)?.label ??
				'Last 24 hours',
			[dateFilter, dateFilterOptions]
		);

		const handleExportCsv = useCallback(() => {
			if (onExportCsv) {
				onExportCsv();
				trackEvent(
					`${analyticsEventPrefix}/export_csv`,
					{
						date_filter: dateFilter,
						date_range_option: dateRangeOption,
					},
					user,
					workspace!
				);
			}
		}, [
			onExportCsv,
			analyticsEventPrefix,
			dateFilter,
			dateRangeOption,
			user,
			workspace,
		]);

		return (
			<Card withBorder style={{ borderRadius: theme.radius.md }}>
				<Stack spacing="md">
					<Group position="apart">
						<Title size="sm" weight="bold">
							{title}
						</Title>
						<Group position="apart" spacing="sm">
							{showExportButton && onExportCsv && (
								<IconButton
									iconName="download"
									variant="tertiary"
									tooltip="Export to CSV"
									onClick={handleExportCsv}
								/>
							)}
							{!hideDateFilter && (
								<Selector
									initialValue={[dateFilter]}
									getItems={getDateFilterItems}
									onChange={handleDateFilterChange}
									selectType={SelectType.Single}
									disableSearch
								>
									<SelectorTarget size="md" variant="default">
										{selectedDateFilterLabel}
									</SelectorTarget>
								</Selector>
							)}
							{!hideDateRangeButtons && (
								<DateRangeButtonGroup
									selectedOptionId={dateRangeOption}
									onChange={handleDateRangeChange}
								/>
							)}
						</Group>
					</Group>
					<MetricsOverviewStackedLineChart
						channels={channels}
						chartData={chartData}
						dateFilter={dateFilter}
						tooltipDataRenderer={tooltipDataRenderer}
						dateRangeOption={dateRangeOption}
						yAxisFormatter={yAxisFormatter}
						onClick={onClick}
						showEmptyStateMessage={showEmptyStateMessage}
						emptyStateMessage={emptyStateMessage}
						maxChannels={maxChannels}
					/>
				</Stack>
			</Card>
		);
	}
);
